import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { dashboardService } from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { queuePageFilter } from "../../utils/listNavigation";

const StatCard = ({ label, value, icon, to, filter, accent }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    if (!to) return;
    e.preventDefault();
    if (filter) queuePageFilter(to, filter);
    navigate(to, filter ? { state: { filter } } : undefined);
  };

  const body = (
    <div className="card h-100 bw-dashboard-stat-card">
      <div className="card-body d-flex align-items-center gap-3">
        <span
          className="bw-dashboard-stat-icon d-flex align-items-center justify-content-center rounded-circle"
          style={{
            background: accent || "var(--bw-primary-50)",
          }}
        >
          <i className={icon} />
        </span>
        <div className="min-w-0">
          <div className="bw-dashboard-stat-value">{value ?? "—"}</div>
          <div className="text-muted small text-uppercase bw-dashboard-stat-label">{label}</div>
        </div>
      </div>
    </div>
  );

  if (!to) return body;

  return (
    <Link
      to={to}
      onClick={handleClick}
      className="bw-dashboard-stat text-decoration-none text-reset d-block h-100"
    >
      {body}
    </Link>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { can } = usePermission();

  useEffect(() => {
    dashboardService
      .getStats()
      .then((res) => setStats(res?.data || null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-lg-4">
          <StatCard
            label="Pending registrations"
            value={loading ? "…" : stats?.pendingRegistrations}
            icon="cil-user-follow"
            to="/families"
            filter="pending"
            accent="#fff4e5"
          />
        </div>
        <div className="col-12 col-sm-6 col-lg-4">
          <StatCard
            label="Pending submissions"
            value={loading ? "…" : stats?.pendingSubmissions}
            icon="cil-inbox"
            to="/submissions"
            filter="pending"
            accent="#e6f6ff"
          />
        </div>
        <div className="col-12 col-sm-6 col-lg-4">
          <StatCard
            label="Active families"
            value={loading ? "…" : stats?.families}
            icon="cil-people"
            to="/families"
            filter="active"
            accent="#ecfdf5"
          />
        </div>
        <div className="col-12 col-sm-6 col-lg-4">
          <StatCard
            label="Admin users"
            value={loading ? "…" : stats?.users}
            icon="cil-user"
            to={can("users") ? "/users" : undefined}
            accent="#f3e8ff"
          />
        </div>
        <div className="col-12 col-sm-6 col-lg-4">
          <StatCard
            label="Centres"
            value={loading ? "…" : stats?.centres}
            icon="cil-location-pin"
            to={can("centres") ? "/centres" : undefined}
            accent="#fef3c7"
          />
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-md-8 col-lg-6 col-xl-5">
          <div className="card h-100 bw-dashboard-breakdown">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Submissions by form</strong>
              {can("submissions") && (
                <Link to="/submissions" className="small text-decoration-none">
                  View all
                </Link>
              )}
            </div>
            <div className="card-body py-2">
              {loading && <p className="text-muted mb-0 px-1 py-2">Loading…</p>}
              {!loading && (!stats?.byForm || stats.byForm.length === 0) && (
                <p className="text-muted mb-0 px-1 py-2">No submissions yet.</p>
              )}
              {!loading &&
                stats?.byForm?.map((f, i) => (
                  <div
                    key={f.form_name}
                    className={`bw-dashboard-breakdown-row${i === stats.byForm.length - 1 ? " border-0" : ""}`}
                  >
                    <span className="bw-dashboard-breakdown-label">{f.form_name}</span>
                    <span className="badge bg-secondary">{f.total}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
